import { bitcoinjs } from '@optimex-xyz/btc-script'
import { broadcastTx, BtcAddressFormat, getAddressFormatFromAddress } from '@optimex-xyz/trade-wallet-sdk'

import { INetwork } from '@/types'
import {
  CustomError,
  generatePsbtSendBtc,
  getPreferredBtcAccount,
  isBtcChain,
  isMobileDevice,
  SUPPORTED_NETWORK,
  WALLET_PHANTOM,
} from '@/utils'
import { BtcAccount, PhantomWalletInstance } from '@/wallets/types/phantom.type'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions } from './types'

function getPhantomProvider(): PhantomWalletInstance | undefined {
  return window.phantom
}

export class PhantomWallet extends BaseWallet {
  name = 'Phantom Wallet'
  key = WALLET_PHANTOM
  private provider: PhantomWalletInstance | null = null
  private accountsChangedListenerBitcoin: ((...args: unknown[]) => void) | null = null
  private connectedAddresses: Record<string, string> = {}
  private connectedPublicKeys: Record<string, string> = {}
  private accounts: BtcAccount[] = []

  private redirectToWallet(): void {
    const currentUrl = window.location.href
    const ref = window.location.origin
    window.location.href = `https://phantom.app/ul/browse/${encodeURIComponent(currentUrl)}?ref=${encodeURIComponent(ref)}`
  }

  private download(): void {
    throw new CustomError(
      'Phantom Wallet is not installed! <a target="_blank" class="underline" href="https://phantom.com/download">Download wallet</a>'
    )
  }

  private getWalletProvider(): PhantomWalletInstance | null {
    const provider = getPhantomProvider()
    if (provider) {
      return provider
    }
    this.download()
    return null
  }

  async initialize() {
    if (!this.provider) {
      this.provider = this.getWalletProvider()
    }
  }

  isInstalled(): boolean {
    if (isMobileDevice()) {
      return true
    }
    const provider = getPhantomProvider()
    return !!provider?.bitcoin
  }

  private async selectDefaultAccounts(accounts: BtcAccount[], networkId: string) {
    this.accounts = accounts.filter((acc, index, self) => index === self.findIndex((a) => a.address === acc.address))
    return await getPreferredBtcAccount(accounts, networkId)
  }

  async connect(
    network: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    await this.initialize()

    if (isMobileDevice() && !this.provider) {
      this.redirectToWallet()
      throw new CustomError('Redirecting to Phantom...')
    }

    if (!this.provider?.bitcoin) {
      throw new CustomError('Phantom is not installed')
    }

    if (!isBtcChain(network)) throw new CustomError(`Network ${network} is not supported`)

    try {
      this.setupEventListenersBitcoin(options)

      const accounts = await this.provider.bitcoin.requestAccounts()

      if (!accounts?.length) {
        throw new CustomError('No Bitcoin accounts available')
      }

      const selectedAccount = await this.selectDefaultAccounts(accounts, network)

      this.setAddress(network, selectedAccount.address, selectedAccount.publicKey)

      return {
        address: selectedAccount.address,
        networkInfo: options.network,
        compressedPublicKey: selectedAccount.publicKey,
      }
    } catch (error) {
      console.error('Bitcoin connection error:', error)
      throw new CustomError(`Failed to connect to Bitcoin`)
    }
  }

  private setAddress(networkId: string, address: string, publicKey: string): void {
    this.connectedAddresses[networkId] = address
    this.connectedPublicKeys[networkId] = publicKey
  }

  private setupEventListenersBitcoin({ onAccountChanged, network, onDisconnected }: IWalletConnectOptions): void {
    if (!this.provider?.bitcoin) return

    this.removeEventListenersBitcoin()

    this.accountsChangedListenerBitcoin = async (...args: unknown[]) => {
      const accounts = args[0] as BtcAccount[]
      if (!accounts?.length) {
        onDisconnected?.(this.key)
        return
      }

      const selectedAccount = await this.selectDefaultAccounts(accounts, network.networkId)
      this.setAddress(network.networkId, selectedAccount.address, selectedAccount.publicKey)
      onAccountChanged?.({
        address: selectedAccount.address,
        networkInfo: network,
      })
    }

    this.provider.bitcoin.on('accountsChanged', this.accountsChangedListenerBitcoin)
  }

  private removeEventListenersBitcoin(): void {
    if (!this.provider?.bitcoin || !this.accountsChangedListenerBitcoin) return

    this.provider.bitcoin.off('accountsChanged', this.accountsChangedListenerBitcoin)
    this.accountsChangedListenerBitcoin = null
  }

  async disconnect(): Promise<void> {
    if (!this.provider) {
      throw new CustomError('Phantom is not installed')
    }

    try {
      if (this.provider.bitcoin) {
        this.removeEventListenersBitcoin()
      }
      this.accounts = []
      this.provider = null
      this.connectedAddresses = {}
      this.connectedPublicKeys = {}
    } catch (error) {
      console.warn('Error during disconnect:', error)
    }
  }

  private async sendBitcoinTransaction(
    fromAddress: string,
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (!this.provider?.bitcoin) {
      throw new CustomError('Bitcoin provider not available')
    }
    if (networkId !== SUPPORTED_NETWORK.BTC) {
      throw new CustomError(`Unsupported network ${networkId}`)
    }

    const addressType = getAddressFormatFromAddress(fromAddress)
    const isTaproot = addressType === BtcAddressFormat.P2TR
    const isNativeSegwit = addressType === BtcAddressFormat.P2WPKH

    if (!isNativeSegwit && !isTaproot) {
      throw new CustomError('Unsupported address format. Only native segwit and taproot addresses are supported.')
    }

    const { psbt, inputsToSign, btcNetwork, selectedUtxos } = await generatePsbtSendBtc({
      toAddress,
      _amount: amount,
      fromAddress,
      networkId,
      publicKey: this.connectedPublicKeys[networkId],
      options,
    })

    const psbtBase64 = psbt.toBase64()
    const psbtBuffer = Buffer.from(psbtBase64, 'base64')
    const psbtData = new Uint8Array(psbtBuffer)

    const formatInputsToSign = inputsToSign.map((item) => ({
      address: item.address,
      signingIndexes: [item.index],
      // Only include sigHash if it's supported by the Phantom API
    }))

    const signedPsbtData = await this.provider.bitcoin.signPSBT(psbtData, { inputsToSign: formatInputsToSign })

    const signedPsbtBase64 = Buffer.from(signedPsbtData).toString('base64')
    const signedPsbt = bitcoinjs.Psbt.fromBase64(signedPsbtBase64, { network: btcNetwork })

    // Finalize all inputs
    for (let i = 0; i < selectedUtxos.length; i++) {
      signedPsbt.finalizeInput(i)
    }

    // Extract transaction and broadcast
    const tx = signedPsbt.extractTransaction()
    const txHex = tx.toHex()

    return await broadcastTx(networkId, txHex)
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    network: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (!this.provider) {
      throw new CustomError('Phantom is not installed')
    }

    if (!isBtcChain(network)) throw new CustomError(`Network ${network} is not supported`)

    const fromAddress = this.connectedAddresses[network]
    if (!fromAddress) {
      throw new CustomError('Bitcoin wallet not connected')
    }

    return await this.sendBitcoinTransaction(fromAddress, toAddress, amount, network, options)
  }

  async getAddress(network: string): Promise<string> {
    return this.connectedAddresses[network] ?? ''
  }

  getAddresses(): string[] {
    return this.accounts.map((acc) => acc.address)
  }

  switchWalletAddress(address: string, networkId: string) {
    const account = this.accounts.find((e) => e.address === address)
    if (account) {
      this.setAddress(networkId, account.address, account.publicKey)
    }
  }

  async signMessage(message: string, networkId: string): Promise<string> {
    if (!this.provider?.bitcoin) {
      throw new CustomError('Bitcoin provider not available')
    }

    if (isBtcChain(networkId)) {
      // Get the Bitcoin address for the current network
      const address = this.connectedAddresses[networkId]
      if (!address) {
        throw new CustomError('Bitcoin wallet not connected or address not found')
      }

      try {
        // Encode the message as Uint8Array as required by Phantom's API
        const encodedMessage = new TextEncoder().encode(message)

        // Call Phantom's signMessage method with the address and encoded message
        const { signature } = await this.provider.bitcoin.signMessage(address, encodedMessage)

        // Convert the Uint8Array signature to a base64 string
        // Helper function to convert bytes to base64
        const bytesToBase64 = (bytes: Uint8Array) => {
          const binString = String.fromCodePoint(...bytes)
          return btoa(binString)
        }

        return bytesToBase64(signature)
      } catch (error: any) {
        console.error('Bitcoin message signing error:', error)
        throw new Error(`Failed to sign Bitcoin message: ${error?.message}`)
      }
    }

    throw new CustomError(`Network ${networkId} is not supported for message signing`)
  }

  async getCompressedPublicKey(network: string): Promise<string> {
    return this.connectedPublicKeys[network] ?? ''
  }

  async signPsbt(psbt: string, network: string, options?: PsbtSignOptions): Promise<string> {
    if (!this.provider?.bitcoin) {
      throw new CustomError('Bitcoin provider not available')
    }
    if (!isBtcChain(network)) throw new CustomError(`Network ${network} is not supported for PSBT signing`)

    if (!options?.toSignInputs?.length) {
      throw new CustomError('toSignInputs are required for PSBT signing')
    }

    try {
      const psbtData = new Uint8Array(Buffer.from(psbt, 'base64'))

      const inputsToSign = options.toSignInputs.map((input) => ({
        address: input.address || this.connectedAddresses[network],
        signingIndexes: [input.index],
        ...((input as any).sigHash !== undefined ? { sigHash: (input as any).sigHash } : {}),
      }))

      const signedPsbtData = await this.provider.bitcoin.signPSBT(psbtData, { inputsToSign })

      return signedPsbtData
    } catch (error: any) {
      console.error('PSBT signing error:', error)
      throw new Error(`Failed to sign PSBT: ${error?.message}`)
    }
  }

  isDetected(): boolean {
    return !!getPhantomProvider()
  }

  priority(): number {
    return getPhantomProvider() || this.isInstalled() ? 0 : 1
  }
}
